import Vue from 'vue';
import { Component, Watch } from 'vue-property-decorator';
import { CreateUserRequest, CustomerProfile, FormType } from '@/models';
import { AccountPageLoadEvent } from '@/services/analytics/events';
import { Routes } from '@/router';
import { ButtonType } from '@/components/CmgButton/cmg-button';
import { PageLoadEventType } from '@/services/analytics/platforms/adobe-analytics/models';

@Component({
  name: 'CmgGuacModeSignInModal',
})
export default class CmgGuacModeSignInModal extends Vue {
  currentCountry = this.$appStore.state.ui.countryProfile.countryCode;
  isCreateAccount = false;
  showForm = true; // a flag to toggle animation when swapping country

  get visible(): boolean {
    return this.$appStore.state.ui.modals.guacModeSignIn.visible;
  }

  get customer(): CustomerProfile | null {
    const profile = this.$appStore.state.customer.profile;
    return profile && !profile.isGuest ? profile : null;
  }

  get modalImageSrc(): string {
    return this.$appStore.state.cms.images.logos.guacmode2022Medalion.default;
  }

  get unlockGuacModeSrc(): string {
    return this.$appStore.state.cms.images.logos.unlockGuacmode.default;
  }

  get countryProfileChange(): string {
    return this.$appStore.state.ui.countryProfile.countryCode;
  }

  get countryCode(): string {
    return this.currentCountry;
  }

  get modalTitle(): string {
    switch (this.formType) {
      case FormType.CreateAccount:
        return 'create an account';
      case FormType.GroupOrderSignIn:
        return 'sign in to create a group order';
      case FormType.PasswordResetRequest:
        return 'forgot password';
      default:
        return 'sign in';
    }
  }

  get args() {
    return this.$appStore.state.ui.modals.guacModeSignIn.args;
  }

  get formType(): FormType {
    return (this.args && this.args.type) || FormType.SignIn;
  }

  // For deciding which animation to use.
  get slideFadeType(): string {
    switch (this.formType) {
      case FormType.CreateAccount:
        return 'slide-fade-create-account';
      default:
        return 'slide-fade-sign-in';
    }
  }

  get isLoggedIn() {
    return this.$services.customer.isLoggedIn() === true;
  }

  get isEnrolledInLoyalty() {
    return this.$services.loyalty.isEnrolledInLoyalty(this.customer);
  }

  get userIsGuacMode(): boolean {
    return this.$appStore.getters.isCustomerGuacMode;
  }

  @Watch('visible', { immediate: true })
  async visibleChanged(newVisible: boolean) {
    if (newVisible) {
      Vue.nextTick(() => {
        (document.getElementsByClassName('sign-in-model-header')[0] as HTMLElement).focus();
      });
    }
  }

  async mounted() {
    if (this.$router.currentRoute.name === Routes.GuacModeSignIn) {
      await this.$services.customer.refreshCustomerProfileInfo(false, true);
      if (this.isLoggedIn) {
        this.onSignIn();
      }
    }
  }

  async onSignIn() {
    this.$services.ui.setModalVisibility('guacModeSignIn', false);
    this.$router.push({ name: Routes.Order });

    if (this.isEnrolledInLoyalty && this.userIsGuacMode) { // already all set :D
      this.$services.ui.setModalVisibility('guacModeVerified', true);
      this.$services.analytics.send(new AccountPageLoadEvent(PageLoadEventType.GuacModeVerified));
    } else {
      this.$services.ui.setModalVisibility('guacModeJoinRewards', true);
      if (this.isEnrolledInLoyalty) {
        this.$services.analytics.send(new AccountPageLoadEvent(PageLoadEventType.VerifyGuacMode));
      } else {
        this.$services.analytics.send(new AccountPageLoadEvent(PageLoadEventType.JoinRewards));
      }
    }
  }

  onClose() {
    this.$router.push({ name: Routes.Rewards });
    this.$services.ui.setModalVisibility('guacModeSignIn', false);
  }

  setCurrentCountry(country: string, prefillValues: CreateUserRequest, setCountry: (country: string) => void) {
    if (country.toLowerCase() === 'ca') {
      this.$services.ui.setModalVisibility('basicError', true, {
        header: `You're Canadian?`,
        content: `The program hasn't made its way up north just yet. Stay tuned and we'll let you know when it's ready.<br><br><a href="/contact-us">See the Rewards FAQ for details</a>`,
        options: [
          {
            text: 'Continue in Canada',
            type: ButtonType.Primary,
            onClick: () => {
              this.$services.ui.setModalVisibility('basicError', false);
              this.$services.ui.setModalVisibility('guacModeSignIn', false);
              this.$router.push({ path: '/order' })
              this.$services.ui.setModalVisibility('signIn', true, { type: FormType.CreateAccount, prefill: prefillValues });
            },
          },
          {
            text: 'Stay in the US',
            type: ButtonType.Secondary,
            onClick: () => {
              this.$services.ui.setModalVisibility('basicError', false);
              setCountry('US');
            },
          }
        ],
      });
    }
  }
}
